"use client"

import type { VariantProps } from "class-variance-authority"
import type { HTMLMotionProps } from "motion/react"
import type { FC, ReactNode, SVGProps } from "react"
import { forwardRef, memo } from "react"
import { cva } from "class-variance-authority"
import { motion } from "motion/react"

import { cn } from "@productlane/lib/src/browser"

import type { IconType } from "../icons"

export const pillVariants = cva(
	"inline-flex flex-none items-center gap-2 rounded-md font-medium select-none",
	{
		variants: {
			variant: {
				// https://tailwindui.com/components/application-ui/elements/badges
				default:
					"bg-gray-50 text-gray-700 ring-black/15 dark:bg-gray-400/10 dark:text-gray-400 dark:ring-black/10",
				"dark-gray":
					"bg-gray-100 text-gray-800 ring-black/5 dark:bg-gray-500/10 dark:text-gray-400 dark:ring-black/10",
				green:
					"bg-green-50 !text-green-600 ring-green-500/40 dark:bg-green-500/10 dark:text-green-400 dark:ring-green-500/20",
				red: "bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-500/10 dark:text-red-400 dark:ring-red-500/20",
				blue: "bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-500/10 dark:text-blue-400 dark:ring-blue-500/20",
				important:
					"bg-white text-gray-500 font-normal ring-black/10 dark:bg-transparent dark:text-gray-400 dark:ring-white/20",
				glass:
					"shadow-sm ring-1 dark:shadow-black ring-black/15 dark:hover:bg-gray-800 bg-gray-25 hover:bg-gray-100 dark:ring-white/10 dark:bg-gray-900 dark:text-gray-200",
				glassPrimary:
					"[background-color:color-mix(in_srgb,rgb(17_24_39)_85%,transparent)] hover:[background-color:color-mix(in_srgb,rgb(31_41_55)_85%,transparent)] dark:[background-color:color-mix(in_srgb,rgb(255_255_255)_25%,transparent)] dark:hover:[background-color:color-mix(in_srgb,rgb(255_255_255)_35%,transparent)] [backdrop-filter:blur(8px)_saturate(140%)] [-webkit-backdrop-filter:blur(8px)_saturate(140%)] [box-shadow:inset_0_0_0_1px_color-mix(in_srgb,rgb(255_255_255)_8%,transparent),inset_1.8px_3px_0px_-2px_color-mix(in_srgb,rgb(255_255_255)_20%,transparent),inset_-2px_-2px_0px_-2px_color-mix(in_srgb,rgb(255_255_255)_18%,transparent),inset_-3px_-8px_1px_-6px_color-mix(in_srgb,rgb(255_255_255)_12%,transparent),inset_-0.3px_-1px_4px_0px_color-mix(in_srgb,rgb(0_0_0)_15%,transparent),inset_-1.5px_2.5px_0px_-2px_color-mix(in_srgb,rgb(0_0_0)_20%,transparent),inset_0px_3px_4px_-2px_color-mix(in_srgb,rgb(0_0_0)_20%,transparent),inset_2px_-6.5px_1px_-4px_color-mix(in_srgb,rgb(0_0_0)_12%,transparent),0px_1px_5px_0px_color-mix(in_srgb,rgb(0_0_0)_12%,transparent),0px_6px_16px_0px_color-mix(in_srgb,rgb(0_0_0)_10%,transparent)] dark:[box-shadow:inset_0_0_0_1px_color-mix(in_srgb,rgb(255_255_255)_15%,transparent),inset_1.8px_3px_0px_-2px_color-mix(in_srgb,rgb(255_255_255)_45%,transparent),inset_-2px_-2px_0px_-2px_color-mix(in_srgb,rgb(255_255_255)_40%,transparent),inset_-3px_-8px_1px_-6px_color-mix(in_srgb,rgb(255_255_255)_30%,transparent),inset_-0.3px_-1px_4px_0px_color-mix(in_srgb,rgb(0_0_0)_8%,transparent),inset_-1.5px_2.5px_0px_-2px_color-mix(in_srgb,rgb(0_0_0)_12%,transparent),inset_0px_3px_4px_-2px_color-mix(in_srgb,rgb(0_0_0)_12%,transparent),inset_2px_-6.5px_1px_-4px_color-mix(in_srgb,rgb(0_0_0)_8%,transparent),0px_1px_5px_0px_color-mix(in_srgb,rgb(0_0_0)_6%,transparent),0px_6px_16px_0px_color-mix(in_srgb,rgb(0_0_0)_5%,transparent)]",
			},
			size: {
				default: "px-2.5 py-0.5 text-[11px]",
				sm: "px-1.5 py-0.5 text-xxs",
			},
			border: {
				borderless: "ring-0",
				default: "ring-1 ring-inset",
				outside: "ring-1",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
			border: "default",
		},
	},
)

export interface PillProps
	extends HTMLMotionProps<"div">, VariantProps<typeof pillVariants> {
	Icon?: LucideIcon | IconType
	RightIcon?: LucideIcon | IconType
	iconClassName?: string
	children: ReactNode
}

export const Pill = memo(
	forwardRef<HTMLDivElement, PillProps>(
		(
			{
				className,
				size,
				border,
				variant,
				children,
				Icon,
				RightIcon,
				iconClassName,
				...props
			},
			ref,
		) => {
			return (
				<motion.div
					ref={ref}
					{...props}
					className={cn(pillVariants({ variant, size, border, className }))}
				>
					{Icon && <Icon className={cn("h-3 w-3", iconClassName)} />}
					{children}
					{RightIcon && <RightIcon className="h-3 w-3" />}
				</motion.div>
			)
		},
	),
)

type LucideIcon = FC<SVGProps<SVGSVGElement>>
